import * as React from 'react'
import { bind } from 'classnames/bind'
import * as styles from '@procore/core-css/src/components/_segmented_controller.scss'

import addSubcomponents from '../utils/addSubcomponents'
import {
  Props,
  PlacementNS,
  ButtonAttributes,
  DivAttributes,
} from '../utils/types'
import {
  OverlayTriggerPlacement,
  OverlayTriggerVariantNS,
} from '../state/OverlayTrigger'
import { Tooltip } from './Tooltip'

const cx = bind(styles)

const isSegment = (node: any): node is React.ReactElement<HTMLButtonElement> =>
  typeof node === 'object' && node.type === Segment

export namespace SegmentedControllerVariantNS {
  export const block = 'block'
  export const none = ''
}

export namespace SegmentedControllerSegmentVariantNS {
  export const active = 'active'
  export const disabled = 'disabled'
  export const none = ''
}

type SegmentedControllerVariant =
  | typeof SegmentedControllerVariantNS.none
  | typeof SegmentedControllerVariantNS.block

export interface SegmentedControllerProps extends Props {
  block?: boolean
  disabled?: boolean
  variant?: SegmentedControllerVariant
}

export interface SegmentedControllerSegmentProps extends Props {
  active?: boolean
  disabled?: boolean
  tooltip?: string | React.ReactElement<Props>
  placement?: OverlayTriggerPlacement
}

const SegmentedController = React.forwardRef<
  HTMLDivElement,
  DivAttributes & SegmentedControllerProps
>(({ children, className, block = false, disabled = false, ...props }, ref) => (
  <div
    ref={ref}
    className={cx(
      'core-segmented-controller',
      {
        [`core-segmented-controller--${
          SegmentedControllerVariantNS.block
        }`]: block,
      },
      className
    )}
    {...props}
  >
    {React.Children.toArray(children)
      .filter(child => child)
      .map((child: any) => {
        if (isSegment(child)) {
          return React.cloneElement(child, {
            disabled: disabled || child.props.disabled,
          })
        }
      })}
  </div>
))

export const Segment = React.forwardRef<
  HTMLButtonElement,
  ButtonAttributes & SegmentedControllerSegmentProps
>(
  (
    {
      active = false,
      children,
      className,
      disabled = false,
      placement = PlacementNS.top,
      tooltip,
      ...props
    },
    ref
  ) => {
    const { Wrapper, wrapperProps } = tooltip
      ? {
          Wrapper: Tooltip,
          wrapperProps: {
            placement,
            trigger: OverlayTriggerVariantNS.hover,
            overlay: tooltip,
          },
        }
      : {
          Wrapper: React.Fragment,
          wrapperProps: {},
        }

    return (
      <Wrapper {...{ ...wrapperProps }}>
        <span
          className={cx(
            'core-segmented-controller__segment',
            {
              [`core-segmented-controller__segment--${
                SegmentedControllerSegmentVariantNS.active
              }`]: active,
              [`core-segmented-controller__segment--${
                SegmentedControllerSegmentVariantNS.disabled
              }`]: disabled,
            },
            className
          )}
        >
          <button
            {...props}
            disabled={disabled || active}
            aria-selected={active}
            ref={ref}
            className={cx('core-segmented-controller__segment__button')}
            type="button"
          >
            <span className={cx('core-segmented-controller__segment__label')}>
              {children}
            </span>
          </button>
        </span>
      </Wrapper>
    )
  }
)

const withState = (config: any) => (Component: any) => (props: any) => {
  const [selectedIndex, setSelected] = React.useState(0)

  return <Component {...{ ...props, selectedIndex, setSelected }} />
}

export const State = withState({})(({ children, ...props }: any) =>
  children(props)
)

SegmentedController.displayName = 'SegmentedController'

Segment.displayName = 'SegmentedController.Segment'

export default addSubcomponents(
  {
    Segment,
    State,
  },
  SegmentedController
)
